/**
 * EngagementTimeline - Read-only timeline of engagement events
 * 
 * This is a LEDGER - append-only, no editing, no deleting.
 * Sourced from lifecycle history + structured activity data.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  History,
  CheckCircle2,
  CreditCard,
  FileText,
  Calendar,
  Upload,
  Clock,
  ArrowRight
} from "lucide-react";
import { buildTimeline, formatTimestamp, EVENT_TYPES } from "@/utils/serviceAStatus";

/**
 * Get icon for event type
 */
const getEventIcon = (eventType) => {
  switch (eventType) {
    case EVENT_TYPES.AGREEMENT_ACCEPTED:
      return <CheckCircle2 className="w-4 h-4" />;
    case EVENT_TYPES.PAYMENT_SUCCEEDED:
    case EVENT_TYPES.PAYMENT_FAILED:
      return <CreditCard className="w-4 h-4" />;
    case EVENT_TYPES.QUESTIONNAIRE_GENERATED:
    case EVENT_TYPES.REPORT_GENERATED:
    case EVENT_TYPES.REPORT_ISSUED:
      return <FileText className="w-4 h-4" />;
    case EVENT_TYPES.INTERVIEW_SCHEDULED:
    case EVENT_TYPES.INTERVIEW_COMPLETED:
      return <Calendar className="w-4 h-4" />;
    case EVENT_TYPES.TRANSCRIPT_UPLOADED:
      return <Upload className="w-4 h-4" />;
    case EVENT_TYPES.STATE_TRANSITION:
      return <ArrowRight className="w-4 h-4" />;
    default:
      return <Clock className="w-4 h-4" />;
  }
};

/**
 * Get color for event type
 */
const getEventColor = (eventType) => {
  switch (eventType) {
    case EVENT_TYPES.AGREEMENT_ACCEPTED:
    case EVENT_TYPES.PAYMENT_SUCCEEDED:
    case EVENT_TYPES.INTERVIEW_COMPLETED:
    case EVENT_TYPES.TRANSCRIPT_UPLOADED:
      return "text-green-400 bg-green-500/10 border-green-500/30";
    case EVENT_TYPES.PAYMENT_FAILED:
      return "text-red-400 bg-red-500/10 border-red-500/30";
    case EVENT_TYPES.QUESTIONNAIRE_GENERATED:
    case EVENT_TYPES.INTERVIEW_SCHEDULED:
      return "text-blue-400 bg-blue-500/10 border-blue-500/30";
    case EVENT_TYPES.REPORT_GENERATED:
    case EVENT_TYPES.REPORT_ISSUED:
      return "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
    case EVENT_TYPES.STATE_TRANSITION:
      return "text-purple-400 bg-purple-500/10 border-purple-500/30";
    default:
      return "text-slate-400 bg-slate-500/10 border-slate-500/30";
  }
};

/**
 * TimelineEntry - Single timeline event
 */
const TimelineEntry = ({ event, isLast }) => {
  const colorClass = getEventColor(event.event_type);
  
  return (
    <div className="flex gap-4 relative">
      {/* Vertical line connector */}
      {!isLast && (
        <div className="absolute left-[17px] top-10 bottom-0 w-px bg-slate-700" />
      )}
      
      {/* Icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center border ${colorClass}`}>
        {getEventIcon(event.event_type)}
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-white font-medium text-sm">{event.label}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {formatTimestamp(event.timestamp)}
            </p>
          </div>
          <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
            {event.actor}
          </Badge>
        </div>
        
        {/* Show state transition details */}
        {event.event_type === EVENT_TYPES.STATE_TRANSITION && event.metadata?.from_state && (
          <p className="text-xs text-slate-500 mt-1">
            {event.metadata.from_state} → {event.metadata.to_state}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * EngagementTimeline - Main component
 */
const EngagementTimeline = ({ ser, interviews = [], artifacts = [] }) => {
  const events = buildTimeline(ser, interviews, artifacts);
  
  if (!events || events.length === 0) {
    return (
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-primary" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-slate-400 py-4">
            No activity recorded yet
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-navy-900/50 border-slate-800" data-testid="engagement-timeline">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <History className="w-5 h-5 text-primary" />
          Activity Timeline
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Read-only ledger • {events.length} event{events.length !== 1 ? "s" : ""}
        </p>
      </CardHeader>
      <CardContent>
        <div className="mt-2">
          {events.map((event, index) => (
            <TimelineEntry
              key={`${event.timestamp}-${event.event_type}-${index}`}
              event={event}
              isLast={index === events.length - 1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default EngagementTimeline;
